import { FC, useEffect, useState } from 'react';

import User from '@/assets/user.svg?react';

import { useFormDataQuery } from '@/api/getters';
import Home from '@/assets/home.svg?react';
import { Hint } from '@/components/ui/Hint';
import CheckboxAccountIsOwner, {
  OWNER_ACCOUNT_IS_OWNER_NAME,
} from '@/features/v2/summaryForms/components/CheckboxAccountIsOwner';
import CheckboxWithoutPESEL from '@/features/v2/summaryForms/components/CheckboxWithoutPESEL';
import CheckboxWithoutStreet from '@/features/v2/summaryForms/components/CheckboxWithoutStreet';
import InputAddress from '@/features/v2/summaryForms/components/InputAdress';
import InputBuildingNumber from '@/features/v2/summaryForms/components/InputBuildingNumber';
import InputCity from '@/features/v2/summaryForms/components/InputCity';
import InputEmail from '@/features/v2/summaryForms/components/InputEmail';
import InputFirstName from '@/features/v2/summaryForms/components/InputFirstName';
import InputFlatNumber from '@/features/v2/summaryForms/components/InputFlatNumber';
import InputLastName from '@/features/v2/summaryForms/components/InputLastName';
import InputNipCompany from '@/features/v2/summaryForms/components/InputNip';
import InputPesel from '@/features/v2/summaryForms/components/InputPesel';
import InputPhone from '@/features/v2/summaryForms/components/InputPhone';
import InputPostalCode from '@/features/v2/summaryForms/components/InputPostalCode';

import { useLingui } from '@lingui/react';
import { useFormContext } from 'react-hook-form';

interface Props {
  isCoOwner?: boolean;
  prefix?: 'owner' | `coowners.${number}`;
  index?: number;
  className?: string;
  defaultValues: {
    phone: string;
    email: string;
    first_name: string;
    last_name: string;
  };
}

const JdgInputs: FC<Props> = ({
  isCoOwner = false,
  prefix = 'owner',
  index,
  className,
  defaultValues,
}) => {
  const { i18n } = useLingui();

  const [theSame] = useState(false);

  const [focused, setFocused] = useState('');
  const { trigger, watch } = useFormContext();
  const { data } = useFormDataQuery();

  useEffect(() => {
    const commonFields = [
      'first_name',
      'last_name',
      'phone',
      'email',
      'address',
      'building_number',
      'zip_code',
      'city',
      'pesel',
    ];

    const fields = [...commonFields, 'nip'];
    fields.forEach((field) => {
      if (data?.payload?.data?.owner) {
        if (data?.payload?.data[prefix]?.[field]?.length > 0) {
          trigger(`${prefix}.${field}`);
        }
      }
    });

    if (index !== undefined && index !== null) {
      const coownersFields = [...commonFields];
      coownersFields.forEach((field) => {
        if (data?.payload?.data?.coowners?.[index]?.[field]?.length > 0) {
          trigger(`${prefix}.${field}`);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAccountOwner = watch(OWNER_ACCOUNT_IS_OWNER_NAME);

  return (
    <div className={className}>
      {!isCoOwner && <h4>{i18n.t('Podaj dane właściciela pojazdu')}</h4>}
      <div
        className={
          'mt-3 flex flex-col gap-5 rounded-md bg-gray-200 p-3.5 md:p-6'
        }
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <p className="h5 flex items-center gap-2 md:col-span-4">
            <User className="size-4 text-blue-600" />
            {i18n.t(
              isCoOwner ? 'Współwłaściciel pojazdu' : 'Właściciel pojazdu'
            )}
          </p>
          {!isCoOwner && (
            <CheckboxAccountIsOwner
              accountPhoneValue={defaultValues.phone}
              accountEmailValue={defaultValues.email}
              accountFirstNameValue={defaultValues.first_name}
              accountLastNameValue={defaultValues.last_name}
            />
          )}
          <Hint icon="bulb" color="blue" className="col-span-4">
            {i18n.t(
              'Zwróć uwagę, aby numer telefonu i adres e-mail był właściwy dla każdego współwłaściciela pojazdu, dzięki temu dokumenty do podpisania dotrą do każdego współwłaściciela.'
            )}
          </Hint>
          <InputFirstName
            name={`${prefix}.first_name`}
            focused={focused}
            setFocused={setFocused}
            autoCompleteOff={(index ?? 0) > 0}
          />
          <InputLastName
            name={`${prefix}.last_name`}
            focused={focused}
            setFocused={setFocused}
            autoCompleteOff={(index ?? 0) > 0}
          />
          <InputPhone
            name={`${prefix}.phone`}
            focused={focused}
            index={index ?? 0}
            setFocused={setFocused}
            autoCompleteOff={(index ?? 0) > 0}
            disabled={isAccountOwner && !isCoOwner}
          />
          <InputEmail
            name={`${prefix}.email`}
            focused={focused}
            index={index ?? 0}
            setFocused={setFocused}
            disabled={isAccountOwner && !isCoOwner}
          />
          {!isCoOwner && (
            <InputNipCompany
              name={`${prefix}.nip`}
              focused={focused}
              setFocused={setFocused}
              className="col-span-4 md:col-span-4"
            />
          )}
          <InputPesel
            name={`${prefix}.pesel`}
            focused={focused}
            setFocused={setFocused}
            index={index}
            disabled={!!watch(`${prefix}.without_pesel`)}
          />
          <CheckboxWithoutPESEL name={`${prefix}.without_pesel`} />
        </div>
        <div className="grid grid-cols-4 gap-3">
          <p className="h5 col-span-4 flex items-center gap-2">
            <Home className="size-4 shrink-0 text-blue-600" />
            {i18n.t(
              isCoOwner
                ? 'Adres zamieszkania'
                : 'Adres zamieszkania właściciela'
            )}
          </p>
          {/* {isCoOwner && (
            <TheSameAddressCheckbox
              recipientType={BUSINESS_TYPE.JDG}
              index={index}
              theSame={theSame}
              setTheSame={setTheSame}
              prefix={prefix}
            />
          )} */}
          <InputPostalCode
            name={`${prefix}.zip_code`}
            disabled={
              (isAccountOwner && !isCoOwner && data?.user?.postal_code) ||
              theSame
            }
            focused={focused}
            setFocused={setFocused}
            autoCompleteOff={(index ?? 0) > 0}
            prefix={prefix}
            controlled
          />
          <InputCity
            name={`${prefix}.city`}
            focused={focused}
            setFocused={setFocused}
            disabled={theSame}
            autoCompleteOff={(index ?? 0) > 0}
            prefix={prefix}
            controlled
          />
          <CheckboxWithoutStreet prefix={prefix} disabled={theSame} />
          <InputAddress
            name={`${prefix}.address`}
            focused={focused}
            setFocused={setFocused}
            disabled={theSame}
            autoCompleteOff={(index ?? 0) > 0}
            prefix={prefix}
            controlled
            creatable
          />
          <div className="col-span-4 grid grid-cols-4 gap-3">
            <InputBuildingNumber
              name={`${prefix}.building_number`}
              focused={focused}
              setFocused={setFocused}
              disabled={theSame}
              autoCompleteOff={(index ?? 0) > 0}
            />
            <InputFlatNumber
              name={`${prefix}.flat_number`}
              focused={focused}
              setFocused={setFocused}
              disabled={theSame}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default JdgInputs;
